import { useEffect, useState, useRef } from "react";
import { Navigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Copy, Package, Users, ClipboardList, X, Loader2, Upload, UserPlus, Gift, Trophy, Ticket } from "lucide-react";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { resolveImg } from "@/components/ProductCard";
import { FLOWER_SIZES, PREROLL_OPTIONS, GROW_METHODS, flowerPriceRange } from "@/lib/sizes";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const inputCls =
  "w-full border border-black/15 bg-white px-3 py-2 text-sm text-[#2b2520] outline-none focus:border-[#c99a63]";
const EMPTY_TERPS = [
  { name: "", percentage: "" },
  { name: "", percentage: "" },
  { name: "", percentage: "" },
];
const EMPTY = {
  name: "", category: "flower", strain_type: "hybrid", description: "",
  thca: 25, d9: 0.3, price: 0,
  terpenes: EMPTY_TERPS,
  prices: { "1g": 0, "8th": 0, "quarter": 0, "half": 0, "oz": 0, "preroll_1g": 0, "preroll_2pk": 0, "preroll_3pk": 0 },
  coa_batch_number: "", coa_lab_name: "", coa_test_date: "", coa_images: [],
  collection: "", grow_method: "",
  image_url: "", stock: 20, featured: false, active: true,
};

function ProductForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(() => {
    const base = initial || EMPTY;
    const terps = base.terpenes && base.terpenes.length
      ? [0, 1, 2].map((i) => base.terpenes[i] || { name: "", percentage: "" })
      : EMPTY_TERPS.map((t) => ({ ...t }));
    return {
      ...EMPTY, ...base,
      prices: { ...EMPTY.prices, ...(base.prices || {}) },
      terpenes: terps,
      coa_images: base.coa_images || [],
    };
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [coaUploading, setCoaUploading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPrice = (k, v) => setForm((f) => ({ ...f, prices: { ...f.prices, [k]: v } }));
  const setTerp = (i, k, v) =>
    setForm((f) => ({ ...f, terpenes: f.terpenes.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)) }));
  const isFlowerCat = form.category === "flower";

  const uploadCoa = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const name = pendingCoaName.current || "COA";
    setCoaUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload-image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setForm((f) => ({ ...f, coa_images: [...(f.coa_images || []), { name, url: data.url }] }));
      }
      toast.success("COA added");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCoaUploading(false);
      pendingCoaName.current = "";
    }
  };

  const coaInputRef = useRef(null);
  const pendingCoaName = useRef("");

  const startCoaUpload = () => {
    const name = window.prompt('Name this COA (e.g. "Gelato — Batch #123")');
    if (name === null) return;
    if (!name.trim()) {
      toast.error("Please enter a name for the COA");
      return;
    }
    pendingCoaName.current = name.trim();
    coaInputRef.current?.click();
  };

  const coaImgUrl = (item) => (typeof item === "string" ? item : item?.url);
  const coaImgName = (item) => (typeof item === "string" ? "" : item?.name || "");
  const removeCoa = (item) =>
    setForm((f) => ({ ...f, coa_images: (f.coa_images || []).filter((u) => coaImgUrl(u) !== coaImgUrl(item)) }));

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("image_url", data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setBusy(true);
    const terps = (form.terpenes || [])
      .filter((t) => t.name && String(t.name).trim())
      .map((t) => ({ name: String(t.name).trim(), percentage: parseFloat(t.percentage) || 0 }));
    const coaFields = {
      coa_batch_number: form.coa_batch_number || "",
      coa_lab_name: form.coa_lab_name || "",
      coa_test_date: form.coa_test_date || "",
      coa_images: form.coa_images || [],
    };
    let payload;
    if (isFlowerCat) {
      const prices = {};
      FLOWER_SIZES.forEach((s) => (prices[s.key] = parseFloat(form.prices?.[s.key]) || 0));
      PREROLL_OPTIONS.forEach((s) => (prices[s.key] = parseFloat(form.prices?.[s.key]) || 0));
      const nonZero = Object.values(prices).filter((v) => v > 0);
      payload = {
        name: form.name, category: "flower", strain_type: form.strain_type || "hybrid",
        description: form.description,
        thca: parseFloat(form.thca) || 0,
        d9: parseFloat(form.d9) || 0,
        terpenes: terps,
        prices,
        price: prices["1g"] || (nonZero.length ? Math.min(...nonZero) : 0),
        collection: form.collection || "", grow_method: form.grow_method || "",
        image_url: form.image_url, featured: form.featured, active: form.active, stock: 100,
        ...coaFields,
      };
    } else {
      payload = {
        name: form.name, category: form.category, strain_type: form.strain_type || "hybrid",
        thca: parseFloat(form.thca) || 0,
        price: parseFloat(form.price) || 0,
        collection: form.collection || "",
        image_url: form.image_url, featured: form.featured, active: form.active,
        stock: parseInt(form.stock) || 0,
        ...coaFields,
      };
    }
    try {
      if (form.id) await api.put(`/admin/products/${form.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Product saved");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-[#2b2520]/40 backdrop-blur-md p-6">
      <div className="my-8 w-full max-w-lg border border-black/10 bg-white p-8 shadow-[0_20px_60px_rgba(43,37,32,0.15)]" data-testid="product-form">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-xl font-bold text-[#2b2520]">{form.id ? "Edit" : "New"} Product</h2>
          <button onClick={onClose} data-testid="close-form" className="text-neutral-500 hover:text-[#2b2520]">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-3">
          <select data-testid="pf-category" className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)}>
            <option value="flower">Flower</option>
            <option value="blunt">Blunt</option>
            <option value="concentrate">Concentrate</option>
          </select>
          <input data-testid="pf-name" className={inputCls} placeholder={isFlowerCat ? "Flower name" : "Name"} value={form.name} onChange={(e) => set("name", e.target.value)} />
          <select data-testid="pf-strain" className={inputCls} value={form.strain_type || "hybrid"} onChange={(e) => set("strain_type", e.target.value)}>
            <option value="indica">Indica</option>
            <option value="sativa">Sativa</option>
            <option value="hybrid">Hybrid</option>
          </select>

          <input
            data-testid="pf-collection"
            className={inputCls}
            placeholder="Collection (e.g. Biggie Smalls) — optional, shows as a home-page carousel"
            value={form.collection || ""}
            onChange={(e) => set("collection", e.target.value)}
          />

          {isFlowerCat && (
            <div>
              <label className="mb-1 block text-[11px] text-neutral-600">Grow method</label>
              <select data-testid="pf-grow" className={inputCls} value={form.grow_method || ""} onChange={(e) => set("grow_method", e.target.value)}>
                <option value="">— Not specified —</option>
                {GROW_METHODS.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>
          )}

          {isFlowerCat ? (
            <>
              <textarea data-testid="pf-desc" className={inputCls} placeholder="Flower description" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} />
              <p className="pt-2 text-xs uppercase tracking-widest text-neutral-500">Potency</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-[11px] text-neutral-600">THCa %</label>
                  <input data-testid="pf-thca" className={inputCls} type="number" step="0.01" placeholder="THCa %" value={form.thca} onChange={(e) => set("thca", e.target.value)} />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] text-neutral-600">Delta-9 (D9) %</label>
                  <input data-testid="pf-d9" className={inputCls} type="number" step="0.01" placeholder="D9 %" value={form.d9} onChange={(e) => set("d9", e.target.value)} />
                </div>
              </div>
              <p className="pt-2 text-xs uppercase tracking-widest text-neutral-500">Top 3 terpenes</p>
              <div className="space-y-2">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="grid grid-cols-3 gap-3">
                    <input
                      data-testid={`pf-terp-name-${i}`}
                      className={`${inputCls} col-span-2`}
                      placeholder={`Terpene ${i + 1} name`}
                      value={form.terpenes[i]?.name || ""}
                      onChange={(e) => setTerp(i, "name", e.target.value)}
                    />
                    <input
                      data-testid={`pf-terp-pct-${i}`}
                      className={inputCls}
                      type="number"
                      step="0.01"
                      placeholder="%"
                      value={form.terpenes[i]?.percentage || ""}
                      onChange={(e) => setTerp(i, "percentage", e.target.value)}
                    />
                  </div>
                ))}
              </div>
              <p className="pt-2 text-xs uppercase tracking-widest text-neutral-500">Price by size</p>
              <div className="grid grid-cols-2 gap-3">
                {FLOWER_SIZES.map((s) => (
                  <div key={s.key}>
                    <label className="mb-1 block text-[11px] text-neutral-600">
                      {s.label} <span className="text-neutral-400">({s.grams})</span>
                    </label>
                    <input
                      data-testid={`pf-price-${s.key}`}
                      className={inputCls}
                      type="number"
                      placeholder="$"
                      value={form.prices[s.key]}
                      onChange={(e) => setPrice(s.key, e.target.value)}
                    />
                  </div>
                ))}
              </div>
              <p className="pt-2 text-xs uppercase tracking-widest text-neutral-500">Preroll options (leave blank to hide)</p>
              <div className="grid grid-cols-2 gap-3">
                {PREROLL_OPTIONS.map((s) => (
                  <div key={s.key}>
                    <label className="mb-1 block text-[11px] text-neutral-600">
                      {s.label}
                    </label>
                    <input
                      data-testid={`pf-price-${s.key}`}
                      className={inputCls}
                      type="number"
                      placeholder="$"
                      value={form.prices[s.key] ?? ""}
                      onChange={(e) => setPrice(s.key, e.target.value)}
                    />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              <input data-testid="pf-thca" className={inputCls} placeholder="THCA %" type="number" value={form.thca} onChange={(e) => set("thca", e.target.value)} />
              <input data-testid="pf-price" className={inputCls} placeholder="Price $" type="number" value={form.price} onChange={(e) => set("price", e.target.value)} />
              <input data-testid="pf-stock" className={inputCls} placeholder="Quantity" type="number" value={form.stock} onChange={(e) => set("stock", e.target.value)} />
            </div>
          )}

          <div>
            <label className="flex cursor-pointer items-center justify-center gap-2 border border-dashed border-black/20 bg-white py-4 text-xs uppercase tracking-widest text-neutral-500 hover:border-[#c99a63]">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {form.image_url ? "Change image" : "Upload image"}
              <input data-testid="pf-image" type="file" accept="image/*" onChange={uploadImage} className="hidden" />
            </label>
            {form.image_url && <img src={resolveImg(form.image_url)} alt="preview" className="mt-2 h-24 w-24 object-cover" />}
          </div>

          <div className="border-t border-black/10 pt-4">
            <p className="mb-3 text-xs uppercase tracking-widest text-neutral-500">Certificate of Analysis (COA)</p>
            <div className="space-y-3">
              <input data-testid="pf-coa-batch" className={inputCls} placeholder="Batch / Lot number" value={form.coa_batch_number} onChange={(e) => set("coa_batch_number", e.target.value)} />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="pf-coa-lab" className={inputCls} placeholder="Testing lab name" value={form.coa_lab_name} onChange={(e) => set("coa_lab_name", e.target.value)} />
                <input data-testid="pf-coa-date" className={inputCls} type="date" placeholder="Test date" value={form.coa_test_date} onChange={(e) => set("coa_test_date", e.target.value)} />
              </div>
              <button
                type="button"
                data-testid="pf-coa-upload-btn"
                onClick={startCoaUpload}
                className="flex w-full cursor-pointer items-center justify-center gap-2 border border-dashed border-black/20 bg-white py-4 text-xs uppercase tracking-widest text-neutral-500 hover:border-[#c99a63]"
              >
                {coaUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Upload COA
              </button>
              <input
                ref={coaInputRef}
                data-testid="pf-coa-upload"
                type="file"
                accept="image/*"
                multiple
                onChange={uploadCoa}
                className="hidden"
              />
              <p className="text-[11px] text-neutral-400">
                Click “Upload COA”, give it a name, then choose a file from your computer or take/select a photo on your phone.
              </p>
              {(form.coa_images || []).length > 0 && (
                <div className="flex flex-wrap gap-3" data-testid="coa-previews">
                  {form.coa_images.map((item) => (
                    <div key={coaImgUrl(item)} className="relative w-24">
                      <img src={resolveImg(coaImgUrl(item))} alt={coaImgName(item) || "COA"} className="h-20 w-24 border border-black/10 object-cover" />
                      <button
                        type="button"
                        onClick={() => removeCoa(item)}
                        className="absolute -right-2 -top-2 bg-[#d32f2f] p-0.5 text-white"
                      >
                        <X className="h-3 w-3" />
                      </button>
                      {coaImgName(item) && (
                        <p className="mt-1 truncate text-[10px] text-neutral-600" title={coaImgName(item)}>{coaImgName(item)}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-neutral-700">
            <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured
          </label>
          <label className="flex items-center gap-2 text-sm text-neutral-700" data-testid="pf-visible-label">
            <input data-testid="pf-visible" type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} /> Visible on site (uncheck to hide when out of stock)
          </label>
          <button data-testid="pf-save" onClick={save} disabled={busy} className="w-full bg-[#c99a63] py-3 text-sm font-semibold uppercase tracking-widest text-[#2b2520] hover:bg-[#bb8850] disabled:opacity-60">
            {busy ? "Saving…" : "Save product"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [viewId, setViewId] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [empForm, setEmpForm] = useState({ name: "", email: "", password: "", role: "employee" });
  const [empBusy, setEmpBusy] = useState(false);
  const [entrants, setEntrants] = useState([]);
  const [winners, setWinners] = useState([]);
  const [drawing, setDrawing] = useState(false);
  const [drawMonth, setDrawMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [drawPrize, setDrawPrize] = useState("Free Ounce");
  const [promos, setPromos] = useState([]);
  const [promoForm, setPromoForm] = useState({ code: "", type: "percent", value: "", expires_at: "", max_uses: "", first_order_only: false });
  const [promoBusy, setPromoBusy] = useState(false);

  const isElevated = ["admin", "owner", "manager"].includes(user?.role);
  const isEmployee = user?.role === "employee";
  const token = localStorage.getItem("nl_token");

  const loadProducts = () => api.get("/admin/products").then(({ data }) => setProducts(data)).catch(() => {});
  const loadOrders = () => api.get("/admin/orders").then(({ data }) => setOrders(data));
  const loadCustomers = () => api.get("/admin/customers").then(({ data }) => setCustomers(data));
  const loadEmployees = () => api.get("/admin/employees").then(({ data }) => setEmployees(data)).catch(() => {});
  const loadGiveaway = () => {
    api.get("/admin/giveaway/entrants").then(({ data }) => setEntrants(data)).catch(() => {});
    api.get("/admin/giveaway/winners").then(({ data }) => setWinners(data)).catch(() => {});
  };
  const loadPromos = () => api.get("/admin/promo-codes").then(({ data }) => setPromos(data)).catch(() => {});

  useEffect(() => {
    if (isEmployee) {
      setTab("orders");
      loadOrders();
    } else if (isElevated) {
      loadProducts();
      loadOrders();
      loadCustomers();
      loadEmployees();
      loadGiveaway();
      loadPromos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (loading) return null;
  if (!user || !["admin", "owner", "manager", "employee"].includes(user.role)) return <Navigate to="/" replace />;

  const removeProduct = async (id) => {
    await api.delete(`/admin/products/${id}`);
    toast.success("Product removed");
    loadProducts();
  };

  const duplicateProduct = (p) => {
    const clone = { ...p };
    delete clone.id;
    clone.name = `${p.name} (Copy)`;
    setEditing({ __prefill: clone, __dupKey: `${p.id}-${Date.now()}` });
  };

  const createPromo = async (e) => {
    e.preventDefault();
    if (!promoForm.code.trim()) {
      toast.error("Enter a promo code");
      return;
    }
    if (promoForm.type !== "free_shipping" && !(parseFloat(promoForm.value) > 0)) {
      toast.error("Enter a discount value");
      return;
    }
    setPromoBusy(true);
    try {
      await api.post("/admin/promo-codes", {
        code: promoForm.code,
        type: promoForm.type,
        value: parseFloat(promoForm.value) || 0,
        expires_at: promoForm.expires_at || null,
        max_uses: promoForm.max_uses ? parseInt(promoForm.max_uses) : null,
        first_order_only: !!promoForm.first_order_only,
      });
      toast.success("Promo code created");
      setPromoForm({ code: "", type: "percent", value: "", expires_at: "", max_uses: "", first_order_only: false });
      loadPromos();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPromoBusy(false);
    }
  };

  const removePromo = async (id) => {
    await api.delete(`/admin/promo-codes/${id}`);
    toast.success("Promo code removed");
    loadPromos();
  };

  const drawWinner = async () => {    setDrawing(true);
    try {
      const { data } = await api.post("/admin/giveaway/draw", { month: drawMonth, prize: drawPrize });
      toast.success(`Winner: ${data.name} (${data.phone})`);
      loadGiveaway();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDrawing(false);
    }
  };

  const setOrderStatus = async (orderId, status) => {
    const fd = new FormData();
    fd.append("status", status);
    await api.put(`/admin/orders/${orderId}/status`, fd);
    toast.success("Order updated");
    loadOrders();
  };

  const verifyCustomer = async (id, status) => {
    await api.put(`/admin/customers/${id}/verify`, { status });
    toast.success(`Customer ${status}`);
    loadCustomers();
  };

  const createEmployee = async (e) => {
    e.preventDefault();
    if (!empForm.name || !empForm.email || !empForm.password) {
      toast.error("Fill in all fields");
      return;
    }
    setEmpBusy(true);
    try {
      await api.post("/admin/employees", empForm);
      toast.success("Employee account created");
      setEmpForm({ name: "", email: "", password: "", role: "employee" });
      loadEmployees();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setEmpBusy(false);
    }
  };

  const removeEmployee = async (id) => {
    try {
      await api.delete(`/admin/employees/${id}`);
      toast.success("Employee removed");
      loadEmployees();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const TABS = isElevated
    ? [
        { key: "products", label: "Products", icon: Package },
        { key: "orders", label: "Orders", icon: ClipboardList },
        { key: "customers", label: "Customers", icon: Users },
        { key: "giveaway", label: "Giveaway", icon: Gift },
        { key: "promos", label: "Promos", icon: Ticket },
        { key: "staff", label: "Employees", icon: UserPlus },
      ]
    : [{ key: "orders", label: "Incoming Orders", icon: ClipboardList }];

  return (
    <div className="mx-auto max-w-6xl px-6 py-12">
      <p className="overline mb-2">Staff Area</p>
      <h1 className="mb-8 text-4xl font-black text-[#2b2520]">Staff Dashboard</h1>

      <div className="mb-8 flex flex-wrap gap-2 border-b border-black/10 pb-4">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`admin-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-5 py-2 text-xs font-semibold uppercase tracking-widest transition-colors ${
              tab === t.key ? "bg-[#c99a63] text-[#2b2520]" : "border border-black/15 text-neutral-600 hover:border-[#c99a63]"
            }`}
          >
            <t.icon className="h-4 w-4" strokeWidth={1.5} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "products" && (
        <div>
          <button
            data-testid="add-product-btn"
            onClick={() => setEditing(EMPTY)}
            className="mb-6 flex items-center gap-2 bg-[#c99a63] px-5 py-2 text-xs font-semibold uppercase tracking-widest text-[#2b2520] hover:bg-[#bb8850]"
          >
            <Plus className="h-4 w-4" /> Add product
          </button>
          <div className="space-y-3" data-testid="admin-products">
            {products.map((p) => (
              <div key={p.id} className={`flex items-center gap-4 border bg-white p-4 ${p.active ? "border-black/10" : "border-black/10 opacity-50"}`}>
                <img src={resolveImg(p.image_url)} alt={p.name} className="h-14 w-14 object-cover" />
                <div className="flex-1">
                  <p className="font-medium text-[#2b2520]">
                    {p.name}
                    {!p.active && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#b5610a]">Hidden</span>}
                  </p>
                  <p className="text-xs text-neutral-500">
                    {p.category === "flower"
                      ? `flower · ${p.strain_type}`
                      : `${p.category} · ${p.strain_type} · ${p.thca}% THCA · ${p.stock} in stock`}
                  </p>
                </div>
                <span className="font-semibold text-[#2b2520]">
                  {p.category === "flower"
                    ? (() => {
                        const r = flowerPriceRange(p);
                        return r ? `from $${r.min.toFixed(2)}` : "—";
                      })()
                    : `$${(p.price || 0).toFixed(2)}`}
                </span>
                <button data-testid={`edit-product-${p.id}`} onClick={() => setEditing(p)} className="text-neutral-500 hover:text-[#a06d34]">
                  <Pencil className="h-4 w-4" strokeWidth={1.5} />
                </button>
                <button data-testid={`duplicate-product-${p.id}`} title="Duplicate" onClick={() => duplicateProduct(p)} className="text-neutral-500 hover:text-[#a06d34]">
                  <Copy className="h-4 w-4" strokeWidth={1.5} />
                </button>
                <button data-testid={`delete-product-${p.id}`} onClick={() => removeProduct(p.id)} className="text-neutral-500 hover:text-[#d32f2f]">
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="space-y-3" data-testid="admin-orders">
          {orders.length === 0 && <p className="text-neutral-500">No orders yet.</p>}
          {orders.map((o) => (
            <div key={o.order_id} className="border border-black/10 bg-white p-5">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <span className="font-mono text-xs text-neutral-500">#{o.order_id.slice(0, 8)}</span>
                  <span className="ml-3 text-sm text-[#2b2520]">{o.user_email}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs" style={{ color: o.payment_status === "paid" ? "#2e7d32" : "#b5610a" }}>
                    {o.payment_status}
                  </span>
                  {isElevated ? (
                    <select
                      data-testid={`order-status-${o.order_id}`}
                      value={o.status}
                      onChange={(e) => setOrderStatus(o.order_id, e.target.value)}
                      className="border border-black/15 bg-white px-2 py-1 text-xs text-[#2b2520]"
                    >
                      {["pending", "processing", "shipped", "delivered", "cancelled"].map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  ) : (
                    <span
                      data-testid={`order-status-badge-${o.order_id}`}
                      className="border border-black/15 px-3 py-1 text-[10px] uppercase tracking-widest text-neutral-600"
                    >
                      {o.status}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-sm text-neutral-600">
                {o.items.map((it, i) => (
                  <span key={i}>{it.name} ×{it.quantity}{i < o.items.length - 1 ? ", " : ""}</span>
                ))}
              </div>
              <p className="mt-2 text-xs text-neutral-500">
                Ship to: {o.shipping_address.full_name}, {o.shipping_address.address1}, {o.shipping_address.city}, {o.shipping_address.state} {o.shipping_address.zip}
              </p>
              <p className="mt-2 font-semibold text-[#2b2520]">${o.total.toFixed(2)}</p>
            </div>
          ))}
        </div>
      )}

      {tab === "customers" && (
        <div className="space-y-3" data-testid="admin-customers">
          {customers.map((c) => (
            <div key={c.id} className="flex flex-wrap items-center gap-4 border border-black/10 bg-white p-4">
              <div className="flex-1">
                <p className="font-medium text-[#2b2520]">{c.name} <span className="text-xs text-neutral-500">({c.age} yrs)</span></p>
                <p className="text-xs text-neutral-500">{c.email} · DOB {c.dob}</p>
                {c.phone && <p className="text-xs text-neutral-500">📞 {c.phone}</p>}
                {c.ai_result && (
                  <p className="mt-1 text-[11px] text-neutral-400">
                    AI: {c.ai_result.is_valid_id ? "valid ID" : "unclear"}{c.ai_result.ai_dob ? `, DOB ${c.ai_result.ai_dob}` : ""}{c.ai_result.age ? `, age ${c.ai_result.age}` : ""}
                  </p>
                )}
              </div>
              {c.id_document_path && (
                <button
                  data-testid={`view-id-${c.id}`}
                  onClick={() => setViewId(`${API}/secure-files/${c.id_document_path}?token=${token}`)}
                  className="border border-black/15 px-3 py-1 text-xs uppercase tracking-widest text-neutral-600 hover:border-[#c99a63]"
                >
                  View ID
                </button>
              )}
              <span
                className="px-3 py-1 text-[10px] uppercase tracking-widest"
                style={{ color: c.verification_status === "approved" ? "#2e7d32" : c.verification_status === "rejected" ? "#d32f2f" : "#b5610a" }}
              >
                {c.verification_status}
              </span>
              <div className="flex gap-2">
                <button data-testid={`approve-${c.id}`} onClick={() => verifyCustomer(c.id, "approved")} className="bg-[#2e7d32] px-3 py-1 text-xs font-semibold uppercase tracking-widest text-white hover:opacity-90">
                  Approve
                </button>
                <button data-testid={`reject-${c.id}`} onClick={() => verifyCustomer(c.id, "rejected")} className="border border-[#d32f2f]/50 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-[#d32f2f] hover:bg-[#d32f2f]/10">
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "giveaway" && (
        <div className="space-y-10" data-testid="admin-giveaway">
          <div className="border border-[#c99a63]/40 bg-[#c99a63]/10 p-6">
            <div className="mb-2 flex items-center gap-2 text-[#a06d34]">
              <Trophy className="h-5 w-5" strokeWidth={1.5} />
              <h2 className="text-lg font-bold text-[#2b2520]">Monthly Free Ounce Giveaway</h2>
            </div>
            <p className="mb-5 text-sm text-neutral-600">
              Every customer who signs up (with a phone number) and places at least one paid order
              is automatically entered. Draw a random winner below — you'll get their name, email,
              and phone so you can reach out.
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="mb-1 block text-[11px] uppercase tracking-widest text-neutral-500">Month</label>
                <input
                  data-testid="giveaway-month"
                  type="month"
                  value={drawMonth}
                  onChange={(e) => setDrawMonth(e.target.value)}
                  className="border border-black/15 bg-white px-3 py-2 text-sm text-[#2b2520] outline-none focus:border-[#c99a63]"
                />
              </div>
              <div>
                <label className="mb-1 block text-[11px] uppercase tracking-widest text-neutral-500">Prize</label>
                <input
                  data-testid="giveaway-prize"
                  value={drawPrize}
                  onChange={(e) => setDrawPrize(e.target.value)}
                  className="border border-black/15 bg-white px-3 py-2 text-sm text-[#2b2520] outline-none focus:border-[#c99a63]"
                />
              </div>
              <button
                data-testid="giveaway-draw-btn"
                onClick={drawWinner}
                disabled={drawing || entrants.length === 0}
                className="flex items-center gap-2 bg-[#c99a63] px-6 py-2 text-xs font-bold uppercase tracking-widest text-[#2b2520] hover:bg-[#bb8850] disabled:opacity-50"
              >
                {drawing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}
                Draw Winner
              </button>
            </div>
          </div>

          <div>
            <h3 className="mb-3 text-sm font-bold uppercase tracking-widest text-[#2b2520]">Past Winners</h3>
            <div className="space-y-2" data-testid="giveaway-winners">
              {winners.length === 0 && <p className="text-sm text-neutral-500">No winners drawn yet.</p>}
              {winners.map((w) => (
                <div key={w.id} className="flex flex-wrap items-center justify-between gap-3 border border-[#2e7d32]/30 bg-[#2e7d32]/5 p-4">
                  <div>
                    <p className="font-semibold text-[#2b2520]">
                      {w.name} <span className="ml-2 text-xs font-normal text-neutral-500">{w.month} · {w.prize}</span>
                    </p>
                    <p className="text-xs text-neutral-600">{w.email} · 📞 {w.phone}</p>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-neutral-400">
                    from {w.entrants_count} entrants
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="mb-3 text-sm font-bold uppercase tracking-widest text-[#2b2520]">
              Eligible Entrants ({entrants.length})
            </h3>
            <div className="space-y-2" data-testid="giveaway-entrants">
              {entrants.length === 0 && (
                <p className="text-sm text-neutral-500">
                  No eligible entrants yet — customers need a phone number and at least one paid order.
                </p>
              )}
              {entrants.map((e) => (
                <div key={e.user_id} className="flex flex-wrap items-center justify-between gap-3 border border-black/10 bg-white p-4">
                  <div>
                    <p className="font-medium text-[#2b2520]">{e.name}</p>
                    <p className="text-xs text-neutral-500">{e.email} · 📞 {e.phone}</p>
                  </div>
                  <span className="text-xs text-neutral-500">{e.orders} paid order{e.orders > 1 ? "s" : ""}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "promos" && (
        <div className="grid gap-10 lg:grid-cols-2" data-testid="admin-promos">
          <div>
            <h2 className="mb-4 text-lg font-bold text-[#2b2520]">Create Promo Code</h2>
            <p className="mb-6 text-sm text-neutral-600">
              Create codes customers can enter at checkout. Percentage off, a fixed dollar
              amount off, or free shipping. Optionally set an expiry date and a max number of uses.
            </p>
            <form onSubmit={createPromo} className="space-y-3" data-testid="promo-form">
              <input
                data-testid="promo-code"
                className={inputCls}
                placeholder="Code (e.g. WELCOME20)"
                value={promoForm.code}
                onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
              />
              <select
                data-testid="promo-type"
                className={inputCls}
                value={promoForm.type}
                onChange={(e) => setPromoForm({ ...promoForm, type: e.target.value })}
              >
                <option value="percent">Percentage off (%)</option>
                <option value="fixed">Fixed amount off ($)</option>
                <option value="free_shipping">Free shipping</option>
              </select>
              {promoForm.type !== "free_shipping" && (
                <input
                  data-testid="promo-value"
                  type="number"
                  step="0.01"
                  className={inputCls}
                  placeholder={promoForm.type === "percent" ? "Percent off (e.g. 20)" : "Dollars off (e.g. 15)"}
                  value={promoForm.value}
                  onChange={(e) => setPromoForm({ ...promoForm, value: e.target.value })}
                />
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-[11px] uppercase tracking-widest text-neutral-500">Expires (optional)</label>
                  <input
                    data-testid="promo-expires"
                    type="date"
                    className={inputCls}
                    value={promoForm.expires_at}
                    onChange={(e) => setPromoForm({ ...promoForm, expires_at: e.target.value })}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] uppercase tracking-widest text-neutral-500">Max uses (optional)</label>
                  <input
                    data-testid="promo-max-uses"
                    type="number"
                    className={inputCls}
                    placeholder="unlimited"
                    value={promoForm.max_uses}
                    onChange={(e) => setPromoForm({ ...promoForm, max_uses: e.target.value })}
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-neutral-700">
                <input
                  data-testid="promo-first-order"
                  type="checkbox"
                  checked={promoForm.first_order_only}
                  onChange={(e) => setPromoForm({ ...promoForm, first_order_only: e.target.checked })}
                  className="accent-[#c99a63]"
                /> First order only (valid only for customers with no paid order yet)
              </label>
              <button
                data-testid="promo-create"
                disabled={promoBusy}
                className="flex w-full items-center justify-center gap-2 bg-[#c99a63] py-3 text-sm font-semibold uppercase tracking-widest text-[#2b2520] hover:bg-[#bb8850] disabled:opacity-60"
              >
                {promoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create code"}
              </button>
            </form>
          </div>

          <div>
            <h2 className="mb-4 text-lg font-bold text-[#2b2520]">Active Codes</h2>
            <div className="space-y-3" data-testid="promo-list">
              {promos.length === 0 && <p className="text-sm text-neutral-500">No promo codes yet.</p>}
              {promos.map((p) => (
                <div key={p.id} className="flex flex-wrap items-center gap-4 border border-black/10 bg-white p-4">
                  <div className="flex-1">
                    <p className="font-mono font-bold text-[#2b2520]">{p.code}</p>
                    <p className="text-xs text-neutral-500">
                      {p.type === "percent" ? `${p.value}% off` : p.type === "fixed" ? `$${Number(p.value).toFixed(2)} off` : "Free shipping"}
                      {p.first_order_only ? " · first order only" : ""}
                      {p.expires_at ? ` · expires ${p.expires_at}` : ""}
                      {p.max_uses ? ` · ${p.used_count}/${p.max_uses} used` : ` · ${p.used_count} used`}
                    </p>
                  </div>
                  <button
                    data-testid={`delete-promo-${p.id}`}
                    onClick={() => removePromo(p.id)}
                    className="text-neutral-500 hover:text-[#d32f2f]"
                  >
                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "staff" && (        <div className="grid gap-10 lg:grid-cols-2" data-testid="admin-staff">
          <div>
            <h2 className="mb-4 text-lg font-bold text-[#2b2520]">Create Account</h2>
            <p className="mb-6 text-sm text-neutral-600">
              Owners, managers, and the admin can create accounts. Owners and
              managers get full dashboard access; employees can only view incoming
              orders.
            </p>
            <form onSubmit={createEmployee} className="space-y-3" data-testid="employee-form">
              <input
                data-testid="emp-name"
                className={inputCls}
                placeholder="Full name"
                value={empForm.name}
                onChange={(e) => setEmpForm({ ...empForm, name: e.target.value })}
              />
              <input
                data-testid="emp-email"
                type="email"
                className={inputCls}
                placeholder="Email"
                value={empForm.email}
                onChange={(e) => setEmpForm({ ...empForm, email: e.target.value })}
              />
              <input
                data-testid="emp-password"
                type="password"
                className={inputCls}
                placeholder="Temporary password"
                value={empForm.password}
                onChange={(e) => setEmpForm({ ...empForm, password: e.target.value })}
              />
              <div>
                <label className="mb-1 block text-xs uppercase tracking-widest text-neutral-500">
                  Role
                </label>
                <select
                  data-testid="emp-role"
                  className={inputCls}
                  value={empForm.role}
                  onChange={(e) => setEmpForm({ ...empForm, role: e.target.value })}
                >
                  <option value="manager">Manager — full access</option>
                  <option value="employee">Employee — view incoming orders only</option>
                </select>
              </div>
              <button
                data-testid="emp-create"
                disabled={empBusy}
                className="flex w-full items-center justify-center gap-2 bg-[#c99a63] py-3 text-sm font-semibold uppercase tracking-widest text-[#2b2520] hover:bg-[#bb8850] disabled:opacity-60"
              >
                {empBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
              </button>
            </form>
          </div>

          <div>
            <h2 className="mb-4 text-lg font-bold text-[#2b2520]">Team</h2>
            <div className="space-y-3" data-testid="staff-list">
              {employees.map((m) => (
                <div key={m.id} className="flex items-center gap-4 border border-black/10 bg-white p-4">
                  <div className="flex-1">
                    <p className="font-medium text-[#2b2520]">{m.name}</p>
                    <p className="text-xs text-neutral-500">{m.email}</p>
                  </div>
                  <span
                    className="px-3 py-1 text-[10px] uppercase tracking-widest"
                    style={{ color: m.role === "admin" ? "#a06d34" : "#8a8178" }}
                  >
                    {m.role}
                  </span>
                  {m.role !== "admin" && (
                    <button
                      data-testid={`remove-emp-${m.id}`}
                      onClick={() => removeEmployee(m.id)}
                      className="text-neutral-500 hover:text-[#d32f2f]"
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}


      {editing && (
        <ProductForm
          key={editing.id || editing.__dupKey || "new"}
          initial={editing.id ? editing : editing.__prefill || null}
          onClose={() => setEditing(null)}
          onSaved={loadProducts}
        />
      )}

      {viewId && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#2b2520]/50 backdrop-blur-md p-6" onClick={() => setViewId(null)} data-testid="id-modal">
          <div className="relative max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setViewId(null)} className="absolute -right-3 -top-3 bg-[#c99a63] p-1 text-[#2b2520]">
              <X className="h-4 w-4" />
            </button>
            <img src={viewId} alt="ID document" className="max-h-[80vh] border border-black/10" />
          </div>
        </div>
      )}
    </div>
  );
}
